import React, { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import { RemoveItemModal } from 'components/modals';
import { Container, ButtonPrimary, ButtonSecondary, Flex } from 'components';
import { accountState, authenticationState, currentRmaState, loadingState } from 'state/atoms';
import {
  Title,
  TotalPrice,
  Table,
  ButtonsWrapper,
  MaximumExceededMessage,
} from './styledComponents';
import {
  MAXIMUM_NO_OF_ITEMS_ON_CASE,
  MAXIMUM_NO_OF_ITEMS_ON_CASE_MESSAGE,
  CLONE_FROM_PARAM,
} from 'utils/constants';
import { useFormatPrice } from 'utils/hooks/useFormatPrice';
import { useLazyQuery } from '@apollo/client';
import { GET_ACCOUNT } from 'connectors/queries/account';
import { useNotifyError, useRmaItemController } from 'utils/hooks';

const NewCaseDetails = () => {
  const [isRemoveItemModalOpen, setIsRemoveItemModalOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const { endCustomerAccountId, accountId } = useRecoilValue(authenticationState);
  const { items, currencyCode } = useRecoilValue(currentRmaState);
  const { resetRma } = useRmaItemController();
  const history = useHistory();
  const setLoading = useSetRecoilState(loadingState);
  const setAccount = useSetRecoilState(accountState);
  const formatPrice = useFormatPrice();

  const notifyAccountError = useNotifyError({
    fallbackMessage: 'Unable to load some of the account data.',
  });

  const [checkAccountCurrency, { data, loading }] = useLazyQuery(GET_ACCOUNT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: notifyAccountError,
    onCompleted: () => {
      const currentAccountCurrency = data.getAccount.defaultCurrencyCode;

      if (currentAccountCurrency !== currencyCode) {
        // Update the account state with the new currency
        setAccount(data.getAccount);
        resetRma();
        notifyCurrencyError();
        return;
      }

      history.push('/cases/new/checkout/shipping');
    },
  });

  const notifyCurrencyError = useNotifyError({
    fallbackMessage: `Account currency (${data?.getAccount?.defaultCurrencyCode}) does not match RMA Case currency (${currencyCode}). Please recreate this case.`,
  });

  useEffect(() => {
    setLoading(loading);

    return () => {
      setLoading(false);
    };
  }, [loading, setLoading]);

  const totalPrice = items.reduce((total, item) => total + item.initialPrice, 0);

  const canAddItems = items.length < MAXIMUM_NO_OF_ITEMS_ON_CASE;

  const handleItemClone = (item) => {
    history.push(`/cases/new/search?${CLONE_FROM_PARAM}=${item.serialNumber}`);
  };

  return (
    <Container>
      <Title>New RMA Case</Title>
      <TotalPrice>Total: {formatPrice(totalPrice, currencyCode)}</TotalPrice>
      <ButtonsWrapper>
        <Flex alignment="center">
          <ButtonSecondary
            disabled={!canAddItems}
            onClick={() => history.push('/cases/new/search')}
          >
            Add RMA Item
          </ButtonSecondary>
          {!canAddItems ? (
            <MaximumExceededMessage>{MAXIMUM_NO_OF_ITEMS_ON_CASE_MESSAGE}</MaximumExceededMessage>
          ) : null}
        </Flex>
        <ButtonPrimary
          disabled={!items.length}
          onClick={() => checkAccountCurrency({ variables: { accountId, endCustomerAccountId } })}
        >
          Checkout
        </ButtonPrimary>
      </ButtonsWrapper>

      <Table
        items={items}
        cloneable
        onItemClone={canAddItems && handleItemClone}
        onItemRemove={(item) => {
          setItemToRemove(item);
          setIsRemoveItemModalOpen(true);
        }}
        currencyCode={currencyCode}
      />
      <RemoveItemModal
        isOpen={isRemoveItemModalOpen}
        item={itemToRemove}
        onClose={() => setIsRemoveItemModalOpen(false)}
      />
    </Container>
  );
};

export { NewCaseDetails };
